"use client";

import React from "react";
import { Activity, Flame, Gauge, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FeatureItem {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  metricLabel: string;
}
const features: FeatureItem[] = [{
  id: "vo2",
  title: "VO₂ Measurement",
  description: "Measures oxygen utilisation as exercise intensity increases.",
  icon: Gauge,
  metricLabel: "O₂ Uptake"
}, {
  id: "co2",
  title: "CO₂ Measurement",
  description: "Adds carbon dioxide measurement during exercise, allowing calculation of respiratory exchange information.",
  icon: Activity,
  metricLabel: "VCO₂ & RER"
}, {
  id: "fuel",
  title: "Exercise Fuel Utilisation",
  description: "Provides information about the relative contribution of fat and carbohydrate at different exercise intensities.",
  icon: Flame,
  metricLabel: "Substrate Ratio"
}, {
  id: "zones",
  title: "Personalised Training Zones",
  description: "Uses the measured exercise response to provide individualised intensity information rather than relying only on generic age-based formulas.",
  icon: Target,
  metricLabel: "Custom Thresholds"
}];
export default function MetabolicFeatures() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="metabolicmeasurementfeatures" data-nav="light" ref={ref} className="bg-background text-foreground py-16 md:py-24 transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-12 md:mb-16">
          <div className="flex justify-center">
            <Badge variant="secondary" className="px-3 py-1 text-xs tracking-wide uppercase font-semibold text-primary">
              Metabolic Physiology
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Advanced metabolic measurement with KORR CardioCoach MAX
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            PhysAnalytica uses the CardioCoach MAX, which measures respiratory gases during exercise and provides objective information about oxygen utilisation, carbon dioxide production and exercise respiratory exchange.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <Card key={feature.id} data-index={i} className="bg-card text-card-foreground border-border hover:border-primary/40 transition-all duration-300 shadow-xs hover:shadow-md flex flex-col justify-between">
                <div>
                  <CardHeader className="pb-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <Icon className="size-5" aria-hidden="true" />
                      </div>
                      <span className="text-[11px] font-mono font-medium text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">
                        {feature.metricLabel}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground pt-1">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </div>
              </Card>;
        })}
        </div>

        <div className="mt-12 text-center">
          <p className="text-xs text-muted-foreground/80 tracking-wide">
            Testing powered by KORR CardioCoach MAX
          </p>
        </div>
      </div>
    </section>;
}